import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { HEALTH, TASK_STATUS, label } from "@/lib/meta";
import { DailyWork } from "@/components/DailyWork";
import { PendingWorkTable } from "@/components/PendingWorkTable";
import { InProgressNow } from "@/components/InProgressNow";
import { UpcomingFestivals } from "@/components/UpcomingFestivals";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Building2, ChevronRight, ListChecks, Play, Check, Camera, Image } from "lucide-react";
import { toast } from "sonner";

function Stat({ v, l, tone = "zinc" }) {
  const map = { zinc: "text-zinc-900", green: "text-emerald-600", orange: "text-amber-600", red: "text-red-600", blue: "text-blue-600" };
  return (
    <div className="text-center">
      <div className={`metric-value text-lg font-semibold ${map[tone]}`}>{v}</div>
      <div className="text-[10px] uppercase tracking-wide text-zinc-400">{l}</div>
    </div>
  );
}

function ClientCard({ c, onOpen }) {
  return (
    <button onClick={onOpen} data-testid={`smm-client-${c.client_id}`} className="w-full rounded-lg border border-zinc-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-zinc-100 text-zinc-500"><Building2 className="h-4 w-4" /></div>
          <div className="font-heading font-semibold text-zinc-900">{c.client_name}</div>
        </div>
        <Badge color={c.health} dot>{HEALTH[c.health]?.label || c.health_label}</Badge>
      </div>
      <div className="mt-3 grid grid-cols-4 gap-1 sm:grid-cols-7">
        <Stat v={c.planned} l="Planned" tone="blue" />
        <Stat v={c.in_progress} l="In Prog" tone="orange" />
        <Stat v={c.ready_for_approval} l="Ready" tone="blue" />
        <Stat v={c.approved} l="Approved" tone="green" />
        <Stat v={c.posted} l="Posted" tone="green" />
        <Stat v={c.pending} l="Pending" tone="orange" />
        <Stat v={c.delayed} l="Delayed" tone="red" />
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-zinc-100 pt-2 text-xs text-zinc-500">
        <span className="flex items-center gap-1.5"><Image className="h-3.5 w-3.5" /> {c.stories} stories this week</span>
        {c.upcoming_shoot && <span className="flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" /> Shoot {fmtDate(c.upcoming_shoot.date)}</span>}
        <span className="flex items-center gap-1 font-medium text-zinc-700">Open <ChevronRight className="h-3.5 w-3.5" /></span>
      </div>
    </button>
  );
}

export default function SmmWorkspace() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["ws-my-clients"], queryFn: async () => (await api.get("/workspace/my-clients")).data });

  const setTask = async (id, status) => { await api.put(`/tasks/${id}`, { status }); toast.success(`Task ${status.replace("_", " ")}`); qc.invalidateQueries({ queryKey: ["ws-my-clients"] }); qc.invalidateQueries({ queryKey: ["my-day"] }); };

  if (isLoading || !data)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;

  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="smm-workspace">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">My Clients & Content</h1>
        <p className="mt-1 text-sm text-zinc-500">Hi {user?.name?.split(" ")[0]} — {t.clients} client{t.clients === 1 ? "" : "s"} assigned to you this week.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Card className="rounded-lg border-zinc-200 p-3 text-center shadow-sm"><div className="metric-value text-2xl font-semibold">{t.planned}</div><div className="text-xs text-zinc-500">Planned</div></Card>
        <Card className="rounded-lg border-zinc-200 p-3 text-center shadow-sm"><div className="metric-value text-2xl font-semibold text-emerald-600">{t.posted}</div><div className="text-xs text-zinc-500">Posted</div></Card>
        <Card className="rounded-lg border-zinc-200 p-3 text-center shadow-sm"><div className="metric-value text-2xl font-semibold text-amber-600">{t.pending}</div><div className="text-xs text-zinc-500">Pending</div></Card>
        <Card className="rounded-lg border-zinc-200 p-3 text-center shadow-sm"><div className="metric-value text-2xl font-semibold text-red-600">{t.delayed}</div><div className="text-xs text-zinc-500">Delayed</div></Card>
        <Card className="rounded-lg border-zinc-200 p-3 text-center shadow-sm"><div className="metric-value text-2xl font-semibold text-blue-600">{t.stories}</div><div className="text-xs text-zinc-500">Stories</div></Card>
      </div>

      <InProgressNow />

      <UpcomingFestivals days={45} showCoverage />

      <PendingWorkTable />

      <div>
        <h3 className="mb-3 font-heading text-base font-semibold">My Clients</h3>
        {data.clients.length === 0 ? (
          <Card className="rounded-lg border-dashed border-zinc-300 py-12 text-center text-sm text-zinc-400">No clients assigned to you yet. Ask your manager to add you on a client.</Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {data.clients.map((c) => <ClientCard key={c.client_id} c={c} onOpen={() => navigate(`/clients/${c.client_id}`)} />)}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="rounded-lg border-zinc-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4">
            <ListChecks className="h-4 w-4 text-zinc-400" /><h3 className="font-heading text-base font-semibold">My General Tasks</h3>
            <Badge color="orange">{data.tasks.length}</Badge>
          </div>
          <div className="space-y-2 p-5">
            {data.tasks.length === 0 && <div className="py-4 text-center text-sm text-zinc-400">No tasks assigned</div>}
            {data.tasks.map((tk) => (
              <div key={tk.id} className="rounded-md border border-zinc-100 p-3" data-testid={`smm-task-${tk.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><div className="text-sm font-medium">{tk.title}</div><div className="text-xs text-zinc-500">Due {fmtDate(tk.due_date)}{tk.client_name ? ` · ${tk.client_name}` : ""}</div></div>
                  <Badge color={TASK_STATUS[tk.display_status]?.color}>{label(TASK_STATUS, tk.display_status)}</Badge>
                </div>
                <div className="mt-2 flex gap-2">
                  {tk.status === "todo" && <Button size="sm" variant="outline" onClick={() => setTask(tk.id, "in_progress")}><Play className="mr-1 h-3 w-3" />Start</Button>}
                  {tk.status !== "completed" && <Button size="sm" variant="outline" onClick={() => setTask(tk.id, "completed")}><Check className="mr-1 h-3 w-3" />Complete</Button>}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <DailyWork />
      </div>
    </div>
  );
}
